import * as React from "react";
import {
  Tabs as ShadcnTabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../tabs";
import { cn } from "@/lib/utils";

// Symbol to identify Tab components
const TAB_SYMBOL = Symbol("Tab");

export interface TabProps {
  tabKey?: string;
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const Tab: React.FC<TabProps> = () => {
  // This component is just a marker - actual rendering is handled by Tabs
  return null;
};

// Mark Tab component
(Tab as any)[TAB_SYMBOL] = true;
Tab.displayName = "Tab";

export interface TabsProps {
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
  defaultValue?: string;
  fullWidth?: boolean;
  disableAnimation?: boolean;
  color?: "default" | "primary" | "secondary";
  variant?: "solid" | "bordered" | "light" | "underlined";
  classNames?: {
    base?: string;
    tabList?: string;
    tab?: string;
    tabContent?: string;
    cursor?: string;
  };
  children?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}

const Tabs = React.forwardRef<React.ElementRef<typeof ShadcnTabs>, TabsProps>(
  (props, ref) => {
    const {
      selectedKey,
      onSelectionChange,
      defaultValue,
      fullWidth,
      disableAnimation,
      color = "default",
      variant = "solid",
      classNames,
      children,
      className,
      "aria-label": ariaLabel,
      ...rest
    } = props;

    const handleValueChange = React.useCallback(
      (value: string) => {
        if (onSelectionChange) {
          onSelectionChange(value);
        }
      },
      [onSelectionChange]
    );

    // Process children to extract tabs
    const tabs = React.useMemo(() => {
      const tabsArray: Array<{
        key: string;
        title: React.ReactNode;
        content: React.ReactNode;
        className?: string;
      }> = [];

      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child)) {
          // Check if it's a Tab component
          const isTab = (child.type as any)?.[TAB_SYMBOL] === true;
          if (isTab) {
            const props = child.props as {
              tabKey?: string;
              title?: React.ReactNode;
              children?: React.ReactNode;
              className?: string;
            };
            const {
              tabKey,
              title,
              children: tabContent,
              className: tabClassName,
            } = props;
            // Use child.key (React key) or tabKey (prop)
            const key = child.key ?? tabKey;
            if (key) {
              tabsArray.push({
                key: String(key),
                title,
                content: tabContent,
                className: tabClassName,
              });
            }
          }
        }
      });

      return tabsArray;
    }, [children]);

    const variantClasses = {
      solid: "",
      bordered: "border",
      light: "",
      underlined: "border-0 rounded-none",
    };

    const colorClasses = {
      default: "",
      primary: "data-[state=active]:text-primary",
      secondary: "data-[state=active]:text-secondary",
    };

    // Get cursor/indicator styles for underlined variant
    const getTabTriggerClasses = (): any => {
      const baseClasses = cn(
        colorClasses[color],
        classNames?.tab,
        disableAnimation && "transition-none"
      );

      if (variant === "underlined") {
        const cursorClasses = classNames?.cursor ?? "bg-primary";
        // For underlined variant, add relative positioning and underline indicator
        return cn(
          baseClasses,
          "relative pb-2",
          "data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:h-0.5",
          cursorClasses.includes("bg-primary") &&
            "data-[state=active]:after:bg-primary",
          cursorClasses.includes("w-full") && "data-[state=active]:after:w-full"
        );
      }

      return baseClasses;
    };

    return (
      <ShadcnTabs
        ref={ref}
        value={selectedKey}
        onValueChange={handleValueChange}
        defaultValue={defaultValue}
        className={cn(classNames?.base, className)}
        {...rest}
      >
        <TabsList
          className={cn(
            variantClasses[variant],
            fullWidth ? "w-full" : "w-fit",
            classNames?.tabList
          )}
          aria-label={ariaLabel}
        >
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.key}
              value={tab.key}
              className={getTabTriggerClasses()}
            >
              {tab.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent
            key={tab.key}
            value={tab.key}
            className={cn(classNames?.tabContent, tab.className)}
          >
            {tab.content}
          </TabsContent>
        ))}
      </ShadcnTabs>
    );
  }
);

Tabs.displayName = "Tabs";

export { Tabs, TabsList, TabsTrigger, TabsContent, Tab };
